import { isEqual, get } from 'lodash-es';
import { useMemo } from 'react';

import {
  Pathable,
  PathsSelectorFrom,
  pathsSelector as realizePaths,
  FormLens,
} from '@tdp/common/form';

/**
 * Hook for tracking if any edits have been made to a set of paths within a form. 
 * 
 * This is useful for marking if a field or a page has any edits that can be saved or reverted. 
 * 
 * @param modelForm the base form lens object
 * @param pathsSelector a list of paths under the modelForm to track for changes
 * @returns a tuple of [hasEdits, editedPaths] where editedPaths is a list of paths that have been edited.
 */
export function useFormEdits<TSchema extends Pathable>(
  modelForm: FormLens<TSchema>,
  pathsSelector: PathsSelectorFrom<TSchema>
) {
  const paths = useMemo(() => realizePaths(pathsSelector), [pathsSelector]);

  const editedPaths = useMemo(
    () => paths.filter((path) => !isEqual(get(modelForm.base, path), get(modelForm.current, path))),
    [modelForm, paths]
  );
  return [editedPaths.length > 0, editedPaths] as const; //const cast so typescript knows it is a tuple.
}
